import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, Package, Blinds, FileText, Hammer } from "lucide-react";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ecrane = (c, canAccess) => {
  const out = c.sursa === "obloane"
    ? (canAccess("obloane") ? [{ key: "obloane", label: "Obloane", icon: Blinds, to: `/obloane?comanda=${c.id}` }] : [])
    : [{ key: "comenzi", label: "Comenzi", icon: Package, to: `/comanda/${c.id}` }];
  if (canAccess("documente")) out.push({ key: "documente", label: "Documente", icon: FileText, to: `/documente?comanda=${c.id}` });
  if (canAccess("manopere")) out.push({ key: "manopere", label: "Manopere", icon: Hammer, to: `/manopere?comanda=${c.id}` });
  return out;
};

const Rezultat = ({ c, canAccess, go }) => (
  <div className="px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`cautare-rezultat-${c.nr_comanda}`}>
    <div className="flex items-center gap-2 flex-wrap text-sm">
      <span className="font-bold text-slate-900">{c.nr_comanda}</span>
      {c.com_erp && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold">ERP {c.com_erp}</span>}
      <span className="text-xs text-slate-500 truncate">{c.client_nume}</span>
    </div>
    <div className="flex flex-wrap gap-2 mt-2">
      {ecrane(c, canAccess).map(({ key, label, icon: Icon, to }) => (
        <button
          key={key}
          data-testid={`cautare-go-${key}-${c.nr_comanda}`}
          onClick={() => go(to)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-amber-400 hover:bg-amber-50 transition-colors"
        >
          <Icon className="h-3.5 w-3.5 text-amber-600" />{label}
        </button>
      ))}
    </div>
  </div>
);

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const { canAccess } = useAuth();

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setItems([]); return; }
    setLoading(true);
    const h = setTimeout(() => {
      apiGet(`/cautare?q=${encodeURIComponent(t)}`)
        .then((d) => setItems(d.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Cauta comanda ERP (304...) sau oferta (Amel-1189)"
        data-testid="global-search-input"
        className="w-full h-10 pl-9 pr-9 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
      />
      {q && (
        <button onClick={() => { setQ(""); setItems([]); }} data-testid="global-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>
      )}
      {open && q.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full max-h-96 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="global-search-results">
          {loading ? (
            <div className="py-6 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="px-4 py-4 text-sm text-slate-500" data-testid="global-search-empty">Nicio comanda gasita.</div>
          ) : (
            items.map((c) => <Rezultat key={c.id} c={c} canAccess={canAccess} go={go} />)
          )}
        </div>
      )}
    </div>
  );
};
